import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Printer, FileSignature } from 'lucide-react';

/**
 * Pre-print dialog for the polished sermon manuscript PDF. Collects the
 * cover-page metadata (date, series name + position, preacher, optional
 * subtitle) and hands the values off to `exportSermonManuscriptPdf`.
 *
 * Defaults:
 *  - date         → today (YYYY-MM-DD)
 *  - series       → reads `series_id` / `series_position` from the sermon
 *                   draft if available
 *  - preacher     → user.name from auth context (parent provides defaultPreacher)
 *  - subtitle     → empty
 *
 * The values are NOT persisted on the draft (manuscript metadata is per-print),
 * but the dialog remembers the last set inputs in localStorage so the next
 * print pre-fills with the same preacher/series.
 */
const STORAGE_KEY = 'lumera.manuscript.last';

const todayIso = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const SermonManuscriptDialog = ({
  open,
  onOpenChange,
  language = 'en',
  defaultSeriesName = '',
  defaultSeriesPosition = '',
  defaultPreacher = '',
  onPrint,
}) => {
  const { t } = useLanguage();
  const [date, setDate] = useState(todayIso());
  const [seriesName, setSeriesName] = useState('');
  const [seriesPosition, setSeriesPosition] = useState('');
  const [preacher, setPreacher] = useState('');
  const [subtitle, setSubtitle] = useState('');

  // Hydrate when dialog opens — last-print snapshot wins, then current draft,
  // then defaults from auth.
  useEffect(() => {
    if (!open) return;
    let last = {};
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) last = JSON.parse(raw) || {};
    } catch {
      last = {};
    }
    setDate(last.date || todayIso());
    setSeriesName(defaultSeriesName || last.series_name || '');
    setSeriesPosition(
      defaultSeriesPosition !== undefined && defaultSeriesPosition !== null && defaultSeriesPosition !== ''
        ? String(defaultSeriesPosition)
        : last.series_position || '',
    );
    setPreacher(defaultPreacher || last.preacher || '');
    setSubtitle(last.subtitle || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handlePrint = () => {
    const info = {
      date,
      series_name: seriesName.trim(),
      series_position: seriesPosition.trim(),
      preacher: preacher.trim(),
      subtitle: subtitle.trim(),
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(info));
    } catch {
      /* ignore quota / private-mode */
    }
    onPrint?.(info);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sermon-manuscript-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-purple-600" />
            {t('sermon.printableManuscript')}
          </DialogTitle>
          <DialogDescription>
            {t('sermon.manuscriptDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          <div>
            <Label htmlFor="ms-date" className="text-xs">
              {t('sermon.sermonDate')}
            </Label>
            <Input
              id="ms-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="ms-date-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label htmlFor="ms-series-name" className="text-xs">
                {t('sermon.series')}
              </Label>
              <Input
                id="ms-series-name"
                value={seriesName}
                onChange={(e) => setSeriesName(e.target.value)}
                placeholder={t('sermon.seriesPlaceholder')}
                data-testid="ms-series-name-input"
              />
            </div>
            <div>
              <Label htmlFor="ms-series-pos" className="text-xs">
                {t('sermon.numberHash')}
              </Label>
              <Input
                id="ms-series-pos"
                value={seriesPosition}
                onChange={(e) => setSeriesPosition(e.target.value.replace(/\D/g, '').slice(0, 3))}
                placeholder="1"
                inputMode="numeric"
                data-testid="ms-series-pos-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="ms-preacher" className="text-xs">
              {t('sermon.preacher')}
            </Label>
            <Input
              id="ms-preacher"
              value={preacher}
              onChange={(e) => setPreacher(e.target.value)}
              placeholder={t('sermon.preacherPlaceholder')}
              data-testid="ms-preacher-input"
            />
          </div>

          <div>
            <Label htmlFor="ms-subtitle" className="text-xs">
              {t('sermon.subtitleOptional')}
            </Label>
            <Input
              id="ms-subtitle"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder={t('sermon.subtitlePlaceholder')}
              data-testid="ms-subtitle-input"
              maxLength={120}
            />
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            data-testid="ms-cancel-btn"
          >
            {t('sermon.cancel')}
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
            data-testid="ms-print-btn"
          >
            <Printer className="w-4 h-4 mr-1.5" />
            {t('sermon.print')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SermonManuscriptDialog;
